import React, { useEffect, useState, useRef } from 'react';
import { Typography, Card, Skeleton, Empty, List, Badge, message, DatePicker, Select, Button, Input, Switch, Modal } from 'antd';
import { WarningOutlined, HistoryOutlined, EditOutlined, AuditOutlined, FolderOpenOutlined, CloseCircleOutlined, EnvironmentOutlined, CompassOutlined, ExclamationCircleOutlined, LoadingOutlined, InfoCircleOutlined, FileTextOutlined, CalendarOutlined, PlusCircleOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getUserName, getUserRole } from '../utils/tokenUtils';
import { APP_CONFIG } from '../utils/constants';
import { BREAKDOWN_STATUS_COLORS } from '../utils/constants';
import { loadMechanicStatuses } from '../services/mechanicStatusService';
import { formatDate } from '../utils/dateUtils';
import { getUnseenChangelogs, markChangelogsAsViewed } from '../utils/changelogUtils';
import ChangelogModal from '../components/ChangelogModal';
import theme from '../assets/styles/theme';
import '../assets/styles/dashboard.css';
import CallMeConfirmModal from '../components/CallMeConfirmModal';
import PanicConfirmModal from '../components/PanicConfirmModal';
import { getCallMeId as getCallMeIdCookie, clearCallMeId as clearCallMeIdCookie, getPanicId as getPanicIdCookie, clearPanicId as clearPanicIdCookie, getUserDetails as getUserDetailsCookie, setUserDetails as setUserDetailsCookie, getAccidentReport as getAccidentReportCookie } from '../utils/cookieUtils';
import { getCallMeById, getPanicById, updateOnDuty, updateLocation, getOnDuty, updateOldUserLocation } from '../utils/apiService';
import breakdownService from '../services/breakdownService';
import { usePermissionAccess, CREATE_DOCUMENT_PERMISSION, NEW_INSPECTIONS_PERMISSION } from '../utils/features';
import {
  getCurrentLocation,
  requestLocationPermission,
  checkLocationPermissionState,
  hasLocationPromptBeenShown,
  PERMISSION_STATES
} from '../utils/locationUtils';


const { Title, Text } = Typography;

const Dashboard = () => {
  const userName = getUserName();
  const userRole = getUserRole();
  const navigate = useNavigate();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [breakdownHistory, setbreakdownHistory] = useState([]);
  const [historyDateRange, setHistoryDateRange] = useState(null); // [start, end]
  const [historyLimit, setHistoryLimit] = useState(20); // 5 | 10 | 20 | 50 | 'all'
  const [hasSearchedHistory, setHasSearchedHistory] = useState(false);
  const [historyRef, setHistoryRef] = useState('');
  const [unseenChangelogs, setUnseenChangelogs] = useState([]);
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [callMeModalVisible, setCallMeModalVisible] = useState(false);
  const [callMeId, setCallMeIdState] = useState(null);
  const [panicModalVisible, setPanicModalVisible] = useState(false);
  const [panicId, setPanicIdState] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isOnDuty, setIsOnDuty] = useState(false);
  const [isUpdateLocationLoading, setIsUpdateLocationLoading] = useState(false);
  const [isUpdateLocationCooling, setIsUpdateLocationCooling] = useState(false);
  const [accidentReport, setAccidentReport] = useState(null);
  const [locationPermissionModalVisible, setLocationPermissionModalVisible] = useState(false);
  const [_locationPermissionState, setLocationPermissionState] = useState(PERMISSION_STATES.UNKNOWN);
  const [locationPermissionLoading, setLocationPermissionLoading] = useState(false);
  const cooldownTimerRef = useRef(null);
  const UPDATE_LOCATION_COOLDOWN_MS = 60 * 1000;
  const [messageApi, contextHolder] = message.useMessage();
  const [mechanicStatuses, setMechanicStatuses] = useState([]);
  const canAccessDocuments = usePermissionAccess(CREATE_DOCUMENT_PERMISSION);
  const canAccessNewInspections = usePermissionAccess(NEW_INSPECTIONS_PERMISSION);

  // Fetch history on demand (Search click)
  const handleSearchHistory = async () => {
    setHasSearchedHistory(true);
    setHistoryLoading(true);
    try {
      const toIso = (d) => d?.toISOString?.() ?? d?.toDate?.()?.toISOString?.();
      const fromDate = toIso(historyDateRange?.[0]);
      const toDate = toIso(historyDateRange?.[1]);
      const list = await breakdownService.getHistoryBreakdowns({
        limit: historyLimit,
        fromDate,
        toDate,
        breakdownReference: historyRef?.trim() || undefined
      });
      setbreakdownHistory(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load breakdown history', err);
      setbreakdownHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Check location permission state when Dashboard mounts
  useEffect(() => {
    const checkLocationPermission = async () => {
      try {
        const permissionState = await checkLocationPermissionState();
        setLocationPermissionState(permissionState);

        // Only show our custom modal if permission is needed and hasn't been shown before
        if ((permissionState === PERMISSION_STATES.PROMPT || permissionState === PERMISSION_STATES.UNKNOWN) &&
          !hasLocationPromptBeenShown()) {
          // Small delay to ensure UI is loaded first
          setTimeout(() => setLocationPermissionModalVisible(true), 500);
        }
      } catch (error) {
        console.warn('Error checking location permission:', error);
      }
    };

    checkLocationPermission();
  }, []);

  useEffect(() => {
    loadMechanicStatuses().then(setMechanicStatuses);
  }, []);

  // We'll update location after permission is explicitly granted via the modal
  // instead of automatically requesting on component mount

  // Check for unseen changelogs and show the modal if needed
  useEffect(() => {
    const checkForChangelogs = () => {
      const unseen = getUnseenChangelogs();
      if (unseen.length > 0) {
        setUnseenChangelogs(unseen);
        setShowChangelogModal(true);
      }
    };

    // Check for changelogs after a short delay to ensure UI is loaded first
    const changelogTimer = setTimeout(checkForChangelogs, 1000);

    return () => clearTimeout(changelogTimer);
  }, []);

  // Restore Call Me state from cookie on load
  useEffect(() => {
    const existingId = getCallMeIdCookie();
    if (existingId) {
      setCallMeIdState(existingId);
    }
  }, []);

  // Restore Panic state from cookie on load
  useEffect(() => {
    const existingId = getPanicIdCookie();
    if (existingId) {
      setPanicIdState(existingId);
    }
  }, []);

  // Load accident report access from cookie (set after login)
  useEffect(() => {
    setAccidentReport(getAccidentReportCookie());
  }, []);

  // Load user details from cookie (set after login)
  useEffect(() => {
    const u = getUserDetailsCookie();
    if (u) {
      setUserDetails(u);
      if (u.OnDuty !== undefined) {
        const onDuty = (u.OnDuty === true) || (u.OnDuty === 'true') || (u.OnDuty === 1) || (u.OnDuty === '1');
        setIsOnDuty(Boolean(onDuty));
      }
    }
  }, []);

  // Bootstrap OnDuty from API and persist in user details cookie (mechanics only)
  useEffect(() => {
    if (userRole !== 'MECHANIC') return;
    let cancelled = false;
    const normalizeOnDuty = (val) => {
      if (typeof val === 'boolean') return val;
      if (val === 1 || val === '1') return true;
      if (val === 0 || val === '0') return false;
      if (typeof val === 'string') return val.toLowerCase() === 'true';
      return Boolean(val);
    };
    (async () => {
      try {
        const data = await getOnDuty();
        const onDuty = normalizeOnDuty(data?.onDuty ?? data?.isOnDuty ?? data?.OnDuty ?? data);
        if (cancelled) return;
        setIsOnDuty(onDuty);
        const current = getUserDetailsCookie() || {};
        setUserDetailsCookie({ ...current, OnDuty: onDuty });
      } catch (err) {
        console.warn('getOnDuty failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [userRole]);

  useEffect(() => {
    return () => {
      try {
        if (cooldownTimerRef.current) {
          clearTimeout(cooldownTimerRef.current);
        }
      } catch {
        // Ignore cooldown cleanup failures.
      }
    };
  }, []);

  // Poll Call Me status while in progress
  useEffect(() => {
    if (!callMeId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const data = await getCallMeById(callMeId);
        const status = data?.pleaseCallStatus;
        if ([2, 3, 4].includes(status)) {
          if (!cancelled) {
            clearCallMeIdCookie();
            setCallMeIdState(null);
            messageApi.success('Call Me completed. You can request again.');
          }
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          if (!cancelled) {
            clearCallMeIdCookie();
            setCallMeIdState(null);
          }
        } else {
          console.error('GetCallMeById failed', err);
        }
      }
    };

    // initial poll and interval
    poll();
    const timer = setInterval(poll, APP_CONFIG.CALL_ME_REFRESH_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [callMeId]);

  // Poll Panic status while in progress
  useEffect(() => {
    if (!panicId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const data = await getPanicById(panicId);
        const status = data?.panicStatus;
        if ([2, 3, 4].includes(status)) {
          if (!cancelled) {
            clearPanicIdCookie();
            setPanicIdState(null);
            messageApi.success('Panic completed. You can send another if needed.');
          }
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          if (!cancelled) {
            clearPanicIdCookie();
            setPanicIdState(null);
          }
        } else {
          console.error('GetPanicById failed', err);
        }
      }
    };

    // initial poll and interval
    poll();
    const timer = setInterval(poll, APP_CONFIG.PANIC_REFRESH_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [panicId]);

  // Handle closing the changelog modal and mark changelogs as viewed
  const handleChangelogClose = () => {
    // Mark all changelogs as viewed
    const changelogVersions = unseenChangelogs.map(log => log.version);
    markChangelogsAsViewed(changelogVersions);

    // Close the modal
    setShowChangelogModal(false);
  };

  const handleOnDutyChange = async (checked) => {
    setIsOnDuty(checked);
    // Persist to user details cookie for consistent gating across app
    const existing = getUserDetailsCookie() || {};
    setUserDetailsCookie({ ...existing, OnDuty: checked });

    // Get location if permissions are granted
    const position = await getCurrentLocation();

    const updatePayload = {
      id: existing?.id,
      onDuty: checked,
      latitude: position?.latitude || '',
      longitude: position?.longitude || ''
    };
    updateOnDuty(updatePayload);
  };

  const handleUpdateLocation = async () => {
    if (isUpdateLocationLoading || isUpdateLocationCooling) {
      return;
    }
    setIsUpdateLocationLoading(true);
    setIsUpdateLocationCooling(true);
    try {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
      cooldownTimerRef.current = setTimeout(() => {
        setIsUpdateLocationCooling(false);
      }, UPDATE_LOCATION_COOLDOWN_MS);

      const position = await getCurrentLocation();

      if (!position) {
        return;
      }

      const updatePayload = {
        id: getUserDetailsCookie()?.id,
        latitude: position.latitude,
        longitude: position.longitude
      };
      await updateLocation(updatePayload);
      messageApi.success('Location updated successfully');
    } catch (err) {
      console.error('UpdateLocation failed', err);
      messageApi.error('Failed to update location');
    } finally {
      setIsUpdateLocationLoading(false);
      cooldownTimerRef.current = setTimeout(() => {
        setIsUpdateLocationCooling(false);
      }, UPDATE_LOCATION_COOLDOWN_MS);
    }
  };

  const handlePanicClick = () => {
    const panicBusy = !!panicId;
    const hasUserDetails = userDetails && userDetails.registerSecura !== undefined && userDetails.registerSecura !== null;
    const panicAllowed = !hasUserDetails || Number(userDetails.registerSecura) === 1;
    if (panicBusy) return;
    if (hasUserDetails && !panicAllowed) {
      messageApi.warning('Please contact your supervisor to enable this service');
      return;
    }
    setPanicModalVisible(true);
  };

  const handleMapClick = () => {
    navigate('/map');
  };


  // Render dashboard based on user role
  const renderDashboardContent = () => {
    // If user is a mechanic, show breakdowns list

    // Default dashboard for other roles
    return (
      <>
        {contextHolder}
        <Text type="secondary" style={{ display: 'block', paddingBottom: '16px' }}>
          Welcome back, {userName}!
        </Text>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ flex: '1', minWidth: '280px' }}>
            <Card
              title="Quick Actions"
              variant="outlined"
              extra={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'column', padding: '8px 0px' }}>
                  {userRole === 'MECHANIC' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text type="secondary">On Duty</Text>
                      <Switch
                        checked={isOnDuty}
                        onChange={handleOnDutyChange}
                      />
                    </div>
                  )}

                </div>
              }
            >
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {/* {(userRole === 'CONSULTANT' || userRole === 'OWNER' || userRole === 'SUPPLIER') && (
                  <div
                    className="action-card"
                    onClick={() => navigate('/createBreakdown')}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      borderRadius: '8px',
                      height: '100px',
                      display: 'flex',
                      flex: '1',
                      minWidth: '120px',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <EditOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                    <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Create Breakdown</div>
                  </div>
                )} */}
                {(userRole === 'CONSULTANT' || userRole === 'OWNER' || userRole === 'SUPPLIER') && (
                  <div
                    className="action-card"
                    onClick={() => navigate('/events/new')}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      borderRadius: '8px',
                      height: '100px',
                      display: 'flex',
                      flex: '1',
                      minWidth: '120px',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <PlusCircleOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                    <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Create Event</div>
                  </div>
                )}
                {/* {(userRole === 'CONSULTANT' || userRole === 'OWNER' || userRole === 'SUPPLIER') && (
                  <div
                    className="action-card"
                    onClick={() => navigate('/breakdownRequests')}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      borderRadius: '8px',
                      height: '100px',
                      display: 'flex',
                      flex: '1',
                      minWidth: '120px',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <AuditOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                    <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Breakdown Requests</div>
                  </div>
                )} */}
                {userRole === 'DRIVER' ? <div
                  className="action-card"
                  onClick={panicId ? () => {
                    messageApi.warning('Panic in progress...');
                  } : () => handlePanicClick()}
                  style={{
                    backgroundColor: '#f5f5f5',
                    color: '#333',
                    borderRadius: '8px',
                    height: '100px',
                    display: 'flex',
                    flex: '1',
                    minWidth: '120px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  {panicId ? <LoadingOutlined style={{ fontSize: '24px', marginBottom: '4px' }} /> : <ExclamationCircleOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />}
                  <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}> {panicId ? 'Panic...' : 'Panic'}</div>
                </div> :
                  null
                  // <div
                  //   className="action-card"
                  //   onClick={() => navigate('/breakdowns')}
                  //   style={{
                  //     backgroundColor: '#f5f5f5',
                  //     color: '#333',
                  //     borderRadius: '8px',
                  //     height: '100px',
                  //     display: 'flex',
                  //     flex: '1',
                  //     minWidth: '120px',
                  //     flexDirection: 'column',
                  //     justifyContent: 'center',
                  //     alignItems: 'center',
                  //     cursor: 'pointer',
                  //     boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  //   }}
                  // >
                  //   <WarningOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                  //   <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Breakdowns</div>
                  // </div>

                }

                {userRole !== 'DRIVER' && (
                  <div
                    className="action-card"
                    onClick={() => navigate('/events')}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      borderRadius: '8px',
                      height: '100px',
                      display: 'flex',
                      flex: '1',
                      minWidth: '120px',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    <CalendarOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                    <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Events</div>
                  </div>
                )}

                {accidentReport && (
                  <div
                    className="action-card"
                    onClick={() => navigate('/accidentReports')}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      borderRadius: '8px',
                      height: '100px',
                      display: 'flex',
                      flex: '1',
                      minWidth: '120px',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <FileTextOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                    <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Accident Report</div>
                  </div>
                )}
                <div
                  className="action-card"
                  onClick={() => navigate('/inspections')}
                  style={{
                    backgroundColor: '#f5f5f5',
                    color: '#333',
                    borderRadius: '8px',
                    height: '100px',
                    display: 'flex',
                    flex: '1',
                    minWidth: '120px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                >
                  <AuditOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                  <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Inspections</div>
                </div>
                {canAccessNewInspections && (
                  <div
                    className="action-card"
                    onClick={() => navigate('/inspections/new')}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      borderRadius: '8px',
                      height: '100px',
                      display: 'flex',
                      flex: '1',
                      minWidth: '120px',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <SafetyCertificateOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                    <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>New Inspections</div>
                  </div>
                )}
                {canAccessDocuments && (
                  <div
                    className="action-card"
                    onClick={() => navigate('/documents')}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      borderRadius: '8px',
                      height: '100px',
                      display: 'flex',
                      flex: '1',
                      minWidth: '120px',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <FolderOpenOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                    <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Documents</div>
                  </div>
                )}
                {(userRole === 'MECHANIC' && isOnDuty) && (
                  <div
                    className="action-card"
                    onClick={(isUpdateLocationLoading || isUpdateLocationCooling) ? undefined : handleUpdateLocation}
                    style={{
                      backgroundColor: (isUpdateLocationLoading || isUpdateLocationCooling) ? '#e0e0e0' : '#f5f5f5',
                      color: (isUpdateLocationLoading || isUpdateLocationCooling) ? '#999' : '#333',
                      borderRadius: '8px',
                      height: '100px',
                      display: 'flex',
                      flex: '1',
                      minWidth: '120px',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: (isUpdateLocationLoading || isUpdateLocationCooling) ? 'not-allowed' : 'pointer',
                      boxShadow: (isUpdateLocationLoading || isUpdateLocationCooling) ? '0 2px 8px rgba(0,0,0,0.05)' : '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    {isUpdateLocationLoading ? (
                      <LoadingOutlined spin style={{ fontSize: '24px', marginBottom: '4px' }} />
                    ) : (
                      <EnvironmentOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                    )}
                    <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Update My Location</div>
                  </div>
                )}
                {(userRole === 'MECHANIC' || userRole === 'SUPPLIER' || userRole === 'OWNER') && (
                  <div
                    className="action-card"
                    onClick={handleMapClick}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      borderRadius: '8px',
                      height: '100px',
                      display: 'flex',
                      flex: '1',
                      minWidth: '120px',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <CompassOutlined style={{ fontSize: '24px', marginBottom: '4px' }} />
                    <div style={{ textAlign: 'center', fontWeight: '500', fontSize: '12px' }}>Map</div>
                  </div>
                )}
              </div>
            </Card>
          </div>
          <div style={{ flex: '1', minWidth: '280px' }}>
            {userRole !== 'DRIVER' && <Card
              title="History"
              bordered={false}
              extra={<HistoryOutlined />}
            >
              {/* Filters */}
              <div className="history-filters" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <DatePicker.RangePicker
                  allowClear
                  value={historyDateRange}
                  onChange={(range) => setHistoryDateRange(range)}
                  style={{ minWidth: 260, flex: 1 }}
                  placement="bottom"
                  popupClassName="mobile-range-picker-dropdown"
                  getPopupContainer={() => document.body}
                  inputReadOnly
                  suffixIcon={<CloseCircleOutlined />}
                  onOpenChange={(open) => {
                    try {
                      if (open) {
                        document.body.classList.add('no-scroll');
                      } else {
                        document.body.classList.remove('no-scroll');
                      }
                    } catch {
                      // Ignore body class cleanup failures.
                    }
                  }}
                />
                <Input
                  allowClear
                  placeholder="Reference (e.g RNR1234XYZ-B)"
                  value={historyRef}
                  onChange={(e) => setHistoryRef(e.target.value)}
                  style={{ flex: 1, minWidth: 200 }}
                  onPressEnter={handleSearchHistory}
                />
                <div className="history-actions">
                  <Select
                    value={historyLimit}
                    onChange={(v) => setHistoryLimit(v)}
                    getPopupContainer={() => document.body}
                    options={[
                      { label: '5', value: 5 },
                      { label: '10', value: 10 },
                      { label: '20', value: 20 },
                      { label: '50', value: 50 },
                      { label: 'All', value: 'all' }
                    ]}
                  />
                  <Button type="primary" onClick={handleSearchHistory}>Search</Button>
                </div>
              </div>

              {historyLoading ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : breakdownHistory.length === 0 ? (
                hasSearchedHistory ? (
                  <Empty description="No breakdown history" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <Empty description="Select a date range and limit, then click Search" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )
              ) : (
                <List
                  dataSource={breakdownHistory}
                  renderItem={(item) => {
                    const breakdownId = item.id ?? item.breakdownId;
                    const rightDate = formatDate(item.dateTimeOffset || item.lastInteractionDate, true);
                    const mechanicStatus = (item.breakdownMechanicStatus !== undefined && item.breakdownMechanicStatus !== null)
                      ? item.breakdownMechanicStatus
                      : (item.breakdownMechanicStatusId !== undefined ? item.breakdownMechanicStatusId : null);
                    const statusText = (mechanicStatus !== null && mechanicStatus !== undefined)
                      ? (mechanicStatuses.find(s => Number(s.id) === Number(mechanicStatus))?.name || 'Unknown')
                      : (item.statusDescription || item.breakdownStatusDescription || item.breakdownStatus || 'Unknown');
                    const statusColor = (mechanicStatus !== null && mechanicStatus !== undefined)
                      ? (BREAKDOWN_STATUS_COLORS[mechanicStatus] || '#999')
                      : '#999';
                    return (
                      <List.Item
                        onClick={() => navigate(`/breakdown/${breakdownId}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong>{item.breakdownReference}</Text>
                            <Text type="secondary">{rightDate}</Text>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                            <Text>{item.registrationNumber}</Text>
                            <Badge color={statusColor} text={statusText} />
                          </div>
                          {item.companyName || item.customerName ? (
                            <div style={{ marginTop: 4 }}>
                              <Text type="secondary">{item.companyName || item.customerName}</Text>
                            </div>
                          ) : null}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              )}
            </Card>}
          </div>
        </div>
      </>
    );
  };

  // Handle requesting location permissions when user clicks Allow button
  const handleRequestLocationPermission = async () => {
    setLocationPermissionLoading(true);
    try {
      // First close our custom modal before the browser's native permission dialog appears
      setLocationPermissionModalVisible(false);

      // Short delay to ensure our modal is gone before browser dialog appears
      await new Promise(resolve => setTimeout(resolve, 300));

      // Now request permission which will trigger the browser's native dialog
      const permissionState = await requestLocationPermission();
      setLocationPermissionState(permissionState);

      if (permissionState === PERMISSION_STATES.GRANTED) {
        messageApi.success('Location access granted');

        // If permission was granted, update user location
        try {
          const location = await getCurrentLocation();
          if (location) {
            const { latitude, longitude } = location;
            const locationPayload = {
              Latitude: latitude,
              Longitude: longitude
            };
            await updateOldUserLocation(locationPayload);
          }
        } catch (error) {
          console.warn('Error updating location after permission grant:', error);
        }
      } else if (permissionState === PERMISSION_STATES.DENIED) {
        messageApi.error('Location access denied. Some features may not work properly.');
      }
    } catch (error) {
      console.error('Error requesting location permission:', error);
      messageApi.error('Failed to request location permission');
    } finally {
      setLocationPermissionLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      {/* Changelog Modal */}
      <ChangelogModal
        visible={showChangelogModal}
        changelogs={unseenChangelogs}
        onClose={handleChangelogClose}
      />

      {/* Call Me Confirm Modal */}
      <CallMeConfirmModal
        visible={callMeModalVisible}
        onCancel={() => setCallMeModalVisible(false)}
        onSubmitted={(id) => {
          setCallMeIdState(id);
          message.success('Call Me submitted');
          setCallMeModalVisible(false);
        }}
      />

      {/* Panic Confirm Modal */}
      <PanicConfirmModal
        visible={panicModalVisible}
        onCancel={() => setPanicModalVisible(false)}
        onSubmitted={(id) => {
          setPanicIdState(id);
          message.success('Panic sent');
          setPanicModalVisible(false);
        }}
      />

      {/* Location Permission Modal */}
      <Modal
        title="Location Access"
        open={locationPermissionModalVisible}
        onCancel={() => setLocationPermissionModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setLocationPermissionModalVisible(false)}>
            Later
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={locationPermissionLoading}
            onClick={handleRequestLocationPermission}
          >
            Allow Location Access
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <InfoCircleOutlined style={{ fontSize: '24px', color: theme.colorPrimary }} />
          <div>
            <p>
              RNR Mobile needs access to your location to provide accurate breakdown assistance and update your position.
            </p>
            <p>
              Your location is only shared when you're logged in.
            </p>
            <p style={{ marginTop: '8px', fontWeight: 'bold' }}>
              Please allow location access for the best experience.
            </p>
          </div>
        </div>
      </Modal>

      {renderDashboardContent()}
    </div>
  );
};

export default Dashboard;
